import React from 'react';
import { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import LoadingPage from '../../components/LoadingPage';
import { useTranslation } from 'react-i18next';
import { Dropdown } from '../../components/PrimereactComponents';
import { redirectErrorPage } from '../../utils/errors';
import Intro from '../../components/common/Intro/Intro';
import Section from '../../components/common/Section/Section';
import DataCards from '../../components/dataCards/DataCards/DataCards';
import { VotingUnityCard } from '../../components/dataCards/dataCardsTemplates/VotingUnityCard';
import { VotingUnityCardDataType } from '../../types';
import { URLKeys } from '../../Constants';

const sortByAgreementPercentage = (
    a: { vote_match: string },
    b: { vote_match: string },
    sortByCode: 'shoda-sestupne' | 'shoda-vzestupne'
) => {
    const parsePercentage = (value: string) => parseFloat(value.replace('%', '').trim());
    const aValue = parsePercentage(a.vote_match);
    const bValue = parsePercentage(b.vote_match);

    return sortByCode === 'shoda-sestupne' ? bValue - aValue : aValue - bValue;
};

function VotingUnity() {
    const [data, setData] = useState<VotingUnityCardDataType[]>([]);
    const [searchParams, setSearchParams] = useSearchParams();
    const { municipalitaId, bodyType, zastupitelstvoId } = useParams();
    const { t } = useTranslation(['translation', 'analyses']);

    const sortByOptions: { name: string; code: 'shoda-sestupne' | 'shoda-vzestupne' }[] = [
        { name: t('largest-agreement', { ns: 'analyses' }), code: 'shoda-sestupne' },
        { name: t('lowest-agreement', { ns: 'analyses' }), code: 'shoda-vzestupne' }
    ];

    const [sortByState, setSortByState] = useState<{
        name: string;
        code: 'shoda-sestupne' | 'shoda-vzestupne';
    }>(sortByOptions.find((option) => option.code === searchParams.get('seradit')) || sortByOptions[0]);

    useEffect(() => {
        setSearchParams(
            (prevParams) => {
                const newParams = new URLSearchParams(prevParams);
                newParams.set(URLKeys.sortBy, sortByState.code);
                return newParams;
            },
            { replace: true }
        );
    }, [setSearchParams, sortByState.code]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const dataResponse = await axios.get(
                    `${municipalitaId}/${bodyType}/${zastupitelstvoId}/votes?pohled=shoda`
                );
                setData(dataResponse.data);
            } catch (error) {
                redirectErrorPage(error);
            }
        };

        fetchData();
    }, [bodyType, municipalitaId, zastupitelstvoId]);

    if (data !== undefined) {
        return (
            <>
                <Intro title={t('voting-unity')} description={t('voting-unity-tooltip', { ns: 'analyses' })} />
                <Section contentClassName='filtering'>
                    <span className='p-float-label'>
                        <Dropdown
                            value={sortByState}
                            options={sortByOptions}
                            onChange={(e) => setSortByState(e.value)}
                            optionLabel='name'
                            inputId='sortBy'
                        />
                        <label htmlFor='sortBy'>{t('sort-by')}</label>
                    </span>
                </Section>
                <Section>
                    <DataCards
                        renderCardCallback={(cardData: VotingUnityCardDataType) => <VotingUnityCard data={cardData} />}
                        getCardUrl={(item) =>
                            `/${municipalitaId}/${bodyType}/${zastupitelstvoId}/hlasovani/${item.id_hlasovani}`
                        }
                        getUniqueId={(item) => item.id_hlasovani}
                        cardsData={(() => {
                            return data.sort((a, b) => sortByAgreementPercentage(a, b, sortByState.code));
                        })()}
                        totalResultText={t('votings-total', {
                            totalCount: data.length
                        })}
                    />
                </Section>
            </>
        );
    } else {
        return <LoadingPage />;
    }
}

export default VotingUnity;
